import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Search, ArrowRight, Clock, BookOpen } from 'lucide-react';
import { BLOG_POSTS, getCategoryColor, formatDate, ROUTE_PATHS } from '@/lib/index';
import { springPresets } from '@/lib/motion';

const ALL_CATEGORIES = ['Todos', ...Array.from(new Set(BLOG_POSTS.map((p) => p.category)))];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: springPresets.gentle },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };

export default function Blog() {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('Todos');
  const listRef = useRef<HTMLDivElement>(null);
  const inView = useInView(listRef, { once: true, margin: '-60px' });

  const filtered = BLOG_POSTS.filter((post) => {
    const matchCat = activeCategory === 'Todos' || post.category === activeCategory;
    const matchQuery =
      !query ||
      post.title.toLowerCase().includes(query.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(query.toLowerCase()) ||
      post.tags.some((t) => t.toLowerCase().includes(query.toLowerCase()));
    return matchCat && matchQuery;
  });

  const featured = BLOG_POSTS[0];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="pt-32 pb-16 border-b border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={springPresets.bouncy}
              className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 text-primary rounded-full px-3 py-1 text-xs font-semibold mb-4"
            >
              <BookOpen className="w-3 h-3" />
              BLOG — CIÊNCIA APLICADA AO MARKETING
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold mb-4 leading-tight"
            >
              Conhecimento que{' '}
              <span className="text-primary">Gera Resultado</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.2 }}
              className="text-muted-foreground text-lg"
            >
              Artigos técnicos sobre IA Preditiva, Engenharia de Dados e Tráfego Pago de alta performance.
            </motion.p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        {/* Featured Post */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.gentle, delay: 0.3 }}
          className="mb-12"
        >
          <p className="text-xs text-muted-foreground font-semibold uppercase tracking-widest mb-4">
            Artigo em Destaque
          </p>
          <Link to={`/blog/${featured.slug}`}>
            <div className="group bg-card border border-border rounded-2xl p-6 md:p-8 hover:border-primary/40 transition-all duration-300">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${getCategoryColor(featured.category)}`}>
                  {featured.category}
                </span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground font-mono">
                  <Clock className="w-3 h-3" />
                  {featured.readTime}
                </span>
                <span className="text-xs text-muted-foreground">{formatDate(featured.date)}</span>
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors leading-snug">
                {featured.title}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4 line-clamp-2">{featured.excerpt}</p>
              <div className="flex items-center gap-2 text-primary text-sm font-semibold">
                Ler artigo completo <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        </motion.div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar artigos..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full bg-card border border-border rounded-xl pl-10 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
            />
          </div>
          {/* Category filter */}
          <div className="flex flex-wrap gap-2">
            {ALL_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                  activeCategory === cat
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card border-border text-muted-foreground hover:border-primary hover:text-primary'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Posts Grid */}
        <motion.div
          ref={listRef}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={stagger}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filtered.length === 0 ? (
            <div className="col-span-full text-center py-16 text-muted-foreground">
              Nenhum artigo encontrado para "<span className="text-foreground">{query}</span>".
            </div>
          ) : (
            filtered.map((post) => (
              <motion.article
                key={post.slug}
                variants={fadeUp}
                whileHover={{ y: -4 }}
                className="bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/40 transition-all duration-300 flex flex-col"
              >
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${getCategoryColor(post.category)}`}>
                      {post.category}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground font-mono">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </span>
                  </div>
                  <h3 className="font-bold text-base leading-snug mb-3 text-foreground line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {post.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="text-xs px-2 py-0.5 bg-muted rounded-full text-muted-foreground border border-border">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                    <span className="text-xs text-muted-foreground">{formatDate(post.date)}</span>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="text-xs text-primary font-semibold flex items-center gap-1 hover:underline"
                    >
                      Ler <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))
          )}
        </motion.div>

        {/* CTA Bottom */}
        <div className="mt-16 text-center border-t border-border pt-12">
          <p className="text-muted-foreground mb-4">Quer aplicar essas estratégias no seu negócio?</p>
          <Link
            to={ROUTE_PATHS.HOME}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-xl text-sm font-bold hover:opacity-90 transition-opacity"
          >
            Conhecer o Protocolo Kamillo 2026 <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
